/**
 * Nearby browse (no q) — sales-point directory card copy.
 * Meta line: category · distance · walk time (mockup peer to Obchody rows).
 */

import type { FormatDistanceMetersLabels } from '../../shared/ui/directoryListFilter.js';
import { formatDistanceMeters } from '../../shared/ui/directoryDistance.js';
import type { DiscoveryIntentSalesPointGroup } from './groupDiscoveryOffersBySalesPoint.js';

/** ~4.8 km/h walking speed — stable client estimate until routing API exists. */
export const NEARBY_WALK_METERS_PER_MINUTE = 80;

export type FormatWalkTimeLabels = {
  readonly minutes: string;
};

export function resolveNearbySalesPointCategoryLabel(
  group: DiscoveryIntentSalesPointGroup,
): string | null {
  for (const offer of group.offers) {
    const name = offer.platformCategory?.name?.trim();
    if (name !== undefined && name.length > 0) {
      return name;
    }
  }
  return null;
}

export function formatWalkTimeFromMeters(
  meters: number | null,
  labels: FormatWalkTimeLabels,
): string | null {
  if (typeof meters !== 'number' || !Number.isFinite(meters) || meters <= 0) {
    return null;
  }
  const minutes = Math.max(1, Math.round(meters / NEARBY_WALK_METERS_PER_MINUTE));
  return labels.minutes.split('{{count}}').join(String(minutes));
}

/**
 * Subtitle segments for nearby SP directory rows (category · distance · walk time).
 */
export function buildNearbySalesPointMetaParts(
  group: DiscoveryIntentSalesPointGroup,
  distanceFormatter: FormatDistanceMetersLabels,
  walkTimeLabels: FormatWalkTimeLabels,
): string[] {
  const parts: string[] = [];
  const category = resolveNearbySalesPointCategoryLabel(group);
  if (category !== null) {
    parts.push(category);
  }
  if (typeof group.distanceMeters === 'number' && Number.isFinite(group.distanceMeters)) {
    parts.push(formatDistanceMeters(group.distanceMeters, distanceFormatter));
  }
  const walkLabel = formatWalkTimeFromMeters(group.distanceMeters, walkTimeLabels);
  if (walkLabel !== null) {
    parts.push(walkLabel);
  }
  return parts;
}

/**
 * Open-hours line — omitted under Frozen Authority **3c** (D25 / G25 evidence IDs).
 * Public SP DTO has no hours field; never treat description as hours.
 */
export function resolveNearbySalesPointOpenHoursLabel(
  _group: DiscoveryIntentSalesPointGroup,
): string | null {
  return null;
}
